/**
 * Account Page - User Profile & Access Pass Management
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Divider,
  Alert,
  IconButton,
  Skeleton,
  Grid,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Star,
  Timer,
  Check,
  Close,
  Edit,
  Warning,
  Lock,
  AccessTime,
  Bolt,
  Construction,
  CardGiftcard,
  GroupAdd,
  ContentCopy,
  Share,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authUtils';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useLanguage } from '../../utils/LanguageContext';
import { updateProfile, redeemCode as redeemCodeApi, getReferralCode, getReferralStats } from '../../utils/api';

const AccountPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const plansRef = useRef(null);
  const { user, loading: authLoading, refreshUser } = useAuth();
  const {
    accessPass,
    timeRemaining,
    usage,
    plans,
    permissions,
    initiatePayment,
    refreshSubscription,
    loading: subLoading,
    hasActivePass,
    isFreePlan,
    getTimeRemainingText,
  } = useSubscription();
  const { language } = useLanguage();

  const [paymentLoading, setPaymentLoading] = useState(null);
  const [error, setError] = useState(null);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [pendingPlanCode, setPendingPlanCode] = useState(null);
  const [success, setSuccess] = useState(null);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [referralShareText, setReferralShareText] = useState({});

  // Fetch referral code + stats on mount
  useEffect(() => {
    if (!user) return;
    const fetchReferral = async () => {
      setReferralLoading(true);
      try {
        const [codeRes, statsRes] = await Promise.all([
          getReferralCode(),
          getReferralStats(),
        ]);
        if (codeRes.success) {
          setReferralCode(codeRes.data.referral_link);
          setReferralShareText({
            en: codeRes.data.share_text_en,
            my: codeRes.data.share_text_my,
          });
        }
        if (statsRes.success) {
          setReferralStats(statsRes.data);
        }
      } catch (err) {
        // Non-critical, silently ignore
      }
      setReferralLoading(false);
    };
    fetchReferral();
  }, [user]);

  // Translations
  const txt = language === 'my' ? {
    title: 'Akaun Saya',
    profile: 'Profil',
    accessPass: 'Pas Akses',
    currentPass: 'Pas Semasa',
    choosePass: 'Dapatkan Pas Akses',
    choosePlan: 'Pilih Pelan Anda',
    yourPlan: 'Pelan Anda',
    upgradeNow: 'Naik Taraf Sekarang',
    bestValue: 'Terbaik',
    currentPlan: 'Pelan Semasa',
    usage: 'Penggunaan',
    getAccess: 'Dapatkan Akses',
    current: 'Aktif',
    recommended: 'Disyorkan',
    oneTimePayment: 'Bayaran sekali - tiada caj berulang',
    free: 'Percuma',
    basicEstimates: 'Anggaran Asas',
    saveProjects: 'Simpan Projek',
    materialEstimator: 'Penganggar Bahan',
    compareScenarios: 'Bandingkan Senario',
    pdfExport: 'Eksport PDF',
    fullBreakdown: 'Pecahan Penuh',
    unlimited: 'Tanpa Had',
    usesOnly: 'guna sahaja',
    upTo: 'Sehingga',
    projects: 'projek',
    memberSince: 'Ahli sejak',
    expiresIn: 'Tamat dalam',
    expired: 'Tamat tempoh',
    passExpiredMsg: 'Pas akses anda telah tamat tempoh',
    noAccount: 'Sila log masuk',
    signIn: 'Log Masuk',
    editPhone: 'Edit Telefon',
    addPhone: 'Tambah Telefon',
    phoneNumber: 'Nombor Telefon',
    phoneRequired: 'Nombor telefon diperlukan untuk pembayaran',
    save: 'Simpan',
    cancel: 'Batal',
    phoneUpdated: 'Telefon dikemas kini',
    noPhone: 'Tambah telefon',
    loading: 'Memuatkan...',
    hours: 'jam',
    days: 'hari',
    passActive: 'Pas Aktif',
    passExpired: 'Pas Tamat',
    freeUser: 'Pengguna Percuma',
    getFullAccess: 'Dapatkan Akses Penuh',
    redeemCode: 'Tebus Kod',
    redeemPlaceholder: 'Masukkan kod tebus',
    redeem: 'Tebus',
    redeemSuccess: 'Kod berjaya ditebus! Pas anda kini aktif.',
    referFriend: 'Jemput Rakan',
    referDescription: 'Jemput rakan dan anda berdua dapat Pas Perancangan 3 hari percuma!',
    shareLink: 'Pautan Rujukan Anda',
    friendsReferred: 'Rakan Dijemput',
    passesEarned: 'Pas Diperolehi',
    linkCopied: 'Pautan disalin!',
    shareViaWhatsApp: 'Kongsi di WhatsApp',
    copyLink: 'Salin Pautan',
  } : {
    title: 'My Account',
    profile: 'Profile',
    accessPass: 'Access Pass',
    currentPass: 'Current Pass',
    choosePass: 'Get Access Pass',
    choosePlan: 'Choose Your Plan',
    yourPlan: 'Your Plan',
    upgradeNow: 'Upgrade Now',
    bestValue: 'Best Value',
    currentPlan: 'Current Plan',
    usage: 'Usage',
    getAccess: 'Get Access',
    current: 'Active',
    recommended: 'Recommended',
    oneTimePayment: 'One-time payment - no recurring charges',
    free: 'Free',
    basicEstimates: 'Basic Estimates',
    saveProjects: 'Save Projects',
    materialEstimator: 'Material Estimator',
    compareScenarios: 'Compare Scenarios',
    pdfExport: 'PDF Export',
    fullBreakdown: 'Full Breakdown',
    unlimited: 'Unlimited',
    usesOnly: 'uses only',
    upTo: 'Up to',
    projects: 'projects',
    memberSince: 'Member since',
    expiresIn: 'Expires in',
    expired: 'Expired',
    passExpiredMsg: 'Your access pass has expired',
    noAccount: 'Please sign in',
    signIn: 'Sign In',
    editPhone: 'Edit Phone',
    addPhone: 'Add Phone',
    phoneNumber: 'Phone Number',
    phoneRequired: 'Phone number required for payment',
    save: 'Save',
    cancel: 'Cancel',
    phoneUpdated: 'Phone updated',
    noPhone: 'Add phone',
    loading: 'Loading...',
    hours: 'hours',
    days: 'days',
    passActive: 'Pass Active',
    passExpired: 'Pass Expired',
    freeUser: 'Free User',
    getFullAccess: 'Get Full Access',
    redeemCode: 'Redeem Code',
    redeemPlaceholder: 'Enter redeem code',
    redeem: 'Redeem',
    redeemSuccess: 'Code redeemed successfully! Your pass is now active.',
    referFriend: 'Refer a Friend',
    referDescription: 'Invite friends and both of you get a free 3-day Planning Pass!',
    shareLink: 'Your Referral Link',
    friendsReferred: 'Friends Referred',
    passesEarned: 'Passes Earned',
    linkCopied: 'Link copied!',
    shareViaWhatsApp: 'Share on WhatsApp',
    copyLink: 'Copy Link',
  };

  const handleGetAccess = async (planCode) => {
    if (planCode === 'free') return;

    if (!user?.phone) {
      setPendingPlanCode(planCode);
      setPhoneDialogOpen(true);
      return;
    }

    setPaymentLoading(planCode);
    setError(null);
    try {
      const response = await initiatePayment(planCode);
      if (response.success && response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        setError(response.message || 'Payment failed');
      }
    } catch (err) {
      if (err.error_code === 'PHONE_REQUIRED') {
        setPendingPlanCode(planCode);
        setPhoneDialogOpen(true);
      } else {
        setError(err.message);
      }
    }
    setPaymentLoading(null);
  };

  const handleSavePhone = async () => {
    if (!phoneInput || phoneInput.length < 9) {
      setError('Invalid phone number');
      return;
    }
    setPhoneLoading(true);
    setError(null);
    try {
      const response = await updateProfile({ phone: phoneInput });
      if (response.success) {
        setSuccess(txt.phoneUpdated);
        setPhoneDialogOpen(false);
        setPhoneInput('');
        await refreshUser();
        if (pendingPlanCode) {
          setTimeout(() => handleGetAccess(pendingPlanCode), 500);
          setPendingPlanCode(null);
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
    setPhoneLoading(false);
  };

  const handleRedeem = async () => {
    if (!redeemInput.trim()) return;
    setRedeemLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await redeemCodeApi(redeemInput.trim());
      if (response.success) {
        setSuccess(response.message || txt.redeemSuccess);
        setRedeemInput('');
        await refreshUser();
        await refreshSubscription();
      } else {
        setError(response.message || 'Failed to redeem code');
      }
    } catch (err) {
      setError(err.message || 'Failed to redeem code');
    }
    setRedeemLoading(false);
  };

  // Not logged in
  if (!authLoading && !user) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Person sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" gutterBottom>{txt.noAccount}</Typography>
        <Button variant="contained" onClick={() => navigate('/auth')}>{txt.signIn}</Button>
      </Box>
    );
  }

  // Loading
  if (authLoading || subLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={150} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const currentPass = accessPass?.type || 'free';

  const getPassColor = (code) => {
    if (code === 'premium') return '#7C3AED';
    if (code === 'weekly') return '#2563EB';
    return '#6B7280';
  };

  const getPassIcon = (code) => {
    if (code === 'premium') return <Star />;
    if (code === 'weekly') return <Bolt />;
    return <Construction />;
  };

  // Calculate progress for countdown
  const getProgressPercent = () => {
    if (!hasActivePass || !accessPass?.duration_hours || !timeRemaining) return 0;
    const totalSeconds = accessPass.duration_hours * 3600;
    const remaining = timeRemaining.total_seconds || 0;
    return Math.max(0, Math.min(100, (remaining / totalSeconds) * 100));
  };

  // Determine if pass is expired (had a pass before but it's no longer active)
  const isExpired = !isFreePlan && !hasActivePass;

  // Plan badge label
  const planBadgeLabel = isFreePlan
    ? txt.freeUser
    : (language === 'my' ? accessPass?.plan_name_my : accessPass?.plan_name) || currentPass;

  const passColor = getPassColor(currentPass);

  // Build feature list for "Your Plan" section
  const p = permissions || {};
  const featureList = [
    {
      key: 'basic_estimates',
      name: txt.basicEstimates,
      included: true,
      detail: p.has_unlimited_estimates ? txt.unlimited : `${usage?.quick_estimates_limit || 2} ${txt.usesOnly}`,
      unlockPlan: null,
    },
    {
      key: 'material_estimator',
      name: txt.materialEstimator,
      included: !!p.can_use_material_estimate,
      detail: null,
      unlockPlan: null,
    },
    {
      key: 'full_breakdown',
      name: txt.fullBreakdown,
      included: !!p.can_use_full_breakdown,
      detail: null,
      unlockPlan: 'WEEKLY+',
    },
    {
      key: 'save_projects',
      name: txt.saveProjects,
      included: !!p.can_save_project,
      detail: p.can_save_project ? `${txt.upTo} ${usage?.max_projects || 3} ${txt.projects}` : null,
      unlockPlan: 'WEEKLY+',
    },
    {
      key: 'pdf_export',
      name: txt.pdfExport,
      included: !!p.can_export_pdf,
      detail: null,
      unlockPlan: 'WEEKLY+',
    },
    {
      key: 'compare_scenarios',
      name: txt.compareScenarios,
      included: !!p.can_compare_scenarios,
      detail: null,
      unlockPlan: 'MONTHLY',
    },
  ];

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Phone Warning */}
      {!user?.phone && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button size="small" color="inherit" onClick={() => setPhoneDialogOpen(true)}>{txt.addPhone}</Button>
        }>
          {txt.phoneRequired}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════════
          SECTION 1: Hero Profile Card
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{
        mb: 3,
        borderLeft: `4px solid ${passColor}`,
        overflow: 'visible',
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}>
            {/* Left: Avatar + User Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{
                width: 72,
                height: 72,
                bgcolor: passColor,
                fontSize: 28,
                fontWeight: 700,
                border: `3px solid ${alpha(passColor, 0.3)}`,
                boxShadow: `0 0 0 3px ${alpha(passColor, 0.1)}`,
              }}>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
                  {user?.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 14, color: user?.phone ? 'text.secondary' : 'warning.main' }} />
                  <Typography
                    variant="body2"
                    color={user?.phone ? 'text.secondary' : 'warning.main'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => { setPhoneInput(user?.phone || ''); setPhoneDialogOpen(true); }}
                  >
                    {user?.phone || txt.noPhone}
                  </Typography>
                  <IconButton size="small" onClick={() => { setPhoneInput(user?.phone || ''); setPhoneDialogOpen(true); }} sx={{ p: 0.25 }}>
                    <Edit sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Right: Plan Badge + Countdown */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              gap: 1,
              minWidth: { sm: 180 },
            }}>
              <Chip
                size="small"
                icon={getPassIcon(currentPass)}
                label={planBadgeLabel}
                sx={{
                  bgcolor: alpha(passColor, 0.1),
                  color: passColor,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: passColor },
                }}
              />

              {/* Active pass countdown */}
              {hasActivePass && timeRemaining && !timeRemaining.expired && (
                <Box sx={{ width: { xs: '100%', sm: 180 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Timer sx={{ fontSize: 16 }} color="primary" />
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      {getTimeRemainingText(language)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressPercent()}
                    sx={{
                      mt: 0.5,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: alpha(passColor, 0.1),
                      '& .MuiLinearProgress-bar': { bgcolor: passColor, borderRadius: 3 },
                    }}
                  />
                </Box>
              )}

              {/* Expired warning */}
              {isExpired && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}>
                  <Warning sx={{ fontSize: 16, color: 'error.main' }} />
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    {txt.passExpiredMsg}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2: "Your Plan" Feature Summary
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {txt.yourPlan}
          </Typography>

          <Grid container spacing={1.5}>
            {featureList.map((feat) => (
              <Grid item xs={6} sm={4} key={feat.key}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: feat.included
                    ? alpha(theme.palette.success.main, 0.04)
                    : alpha(theme.palette.action.disabled, 0.04),
                  border: '1px solid',
                  borderColor: feat.included
                    ? alpha(theme.palette.success.main, 0.12)
                    : 'divider',
                  height: '100%',
                }}>
                  {feat.included ? (
                    <Check sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
                  ) : (
                    <Lock sx={{ fontSize: 18, color: 'action.disabled', mt: 0.25, flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: feat.included ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}
                    >
                      {feat.name}
                    </Typography>
                    {feat.included && feat.detail && (
                      <Typography variant="caption" color="text.secondary">
                        {feat.detail}
                      </Typography>
                    )}
                    {!feat.included && feat.unlockPlan && (
                      <Chip
                        label={feat.unlockPlan}
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Upgrade CTA for free/expired users */}
          {(isFreePlan || isExpired) && (
            <Button
              variant="contained"
              fullWidth
              onClick={scrollToPlans}
              sx={{
                mt: 2.5,
                py: 1.2,
                fontWeight: 700,
                fontSize: '0.95rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #7C3AED 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #6D28D9 100%)`,
                },
              }}
            >
              {txt.upgradeNow}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2.5: Refer a Friend
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{
        mb: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.15),
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Avatar sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              width: 40,
              height: 40,
            }}>
              <GroupAdd />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {txt.referFriend}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {txt.referDescription}
              </Typography>
            </Box>
          </Box>

          {/* Referral Link */}
          {referralLoading ? (
            <Skeleton height={56} sx={{ borderRadius: 2 }} />
          ) : referralCode ? (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                {txt.shareLink}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={referralCode}
                  InputProps={{
                    readOnly: true,
                    sx: { fontSize: '0.85rem', fontWeight: 500 },
                  }}
                />
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(referralCode);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  sx={{
                    bgcolor: linkCopied ? 'success.main' : 'primary.main',
                    color: 'white',
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    '&:hover': { bgcolor: linkCopied ? 'success.dark' : 'primary.dark' },
                  }}
                >
                  {linkCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </Box>

              {linkCopied && (
                <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                  {txt.linkCopied}
                </Typography>
              )}

              {/* Share Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Share />}
                  onClick={() => {
                    const shareText = language === 'my' ? referralShareText.my : referralShareText.en;
                    const waUrl = 'https://wa.me/?text=' + encodeURIComponent(shareText);
                    window.open(waUrl, '_blank');
                  }}
                  sx={{
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#1da851' },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {txt.shareViaWhatsApp}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={() => {
                    const shareText = language === 'my' ? referralShareText.my : referralShareText.en;
                    navigator.clipboard.writeText(shareText);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {txt.copyLink}
                </Button>
              </Box>

              {/* Stats */}
              {referralStats && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  }}>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {referralStats.total_referrals}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {txt.friendsReferred}
                    </Typography>
                  </Box>
                  <Box sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.06),
                  }}>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {Math.floor(referralStats.total_hours_earned / 72)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {txt.passesEarned}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 3: Choose Your Plan (3-column comparison)
          ═══════════════════════════════════════════════════════ */}
      <Box ref={plansRef} sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
          {txt.choosePlan}
        </Typography>

        <Alert severity="info" icon={<AccessTime />} sx={{ mb: 2.5 }}>
          {txt.oneTimePayment}
        </Alert>

        {plans && plans.length > 0 ? (
          <Grid container spacing={2}>
            {plans.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((plan) => {
              const isCurrent = plan.plan_code === currentPass && hasActivePass;
              const isBestValue = plan.plan_code === 'premium';
              const color = getPassColor(plan.plan_code);
              const features = plan.features || {};
              const limits = plan.limits || {};

              return (
                <Grid item xs={12} sm={4} key={plan.plan_code}>
                  <Card
                    sx={{
                      height: '100%',
                      border: isBestValue ? `2px solid ${color}` : '1px solid',
                      borderColor: isBestValue ? color : 'divider',
                      position: 'relative',
                      transition: 'all 0.2s',
                      bgcolor: isBestValue ? alpha(color, 0.02) : undefined,
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                    }}
                  >
                    {/* Best Value badge */}
                    {isBestValue && (
                      <Chip
                        label={txt.bestValue}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: color,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    {/* Current Plan badge */}
                    {isCurrent && (
                      <Chip
                        label={txt.currentPlan}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'success.main',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 2.5, pt: isBestValue || isCurrent ? 3 : 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {/* Plan Icon + Name */}
                      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                        <Avatar sx={{
                          width: 44,
                          height: 44,
                          mx: 'auto',
                          mb: 1,
                          bgcolor: alpha(color, 0.1),
                          color,
                        }}>
                          {getPassIcon(plan.plan_code)}
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {language === 'my' ? plan.name_my || plan.name_en : plan.name_en}
                        </Typography>
                        {plan.plan_code !== 'free' && plan.duration_hours && (
                          <Chip
                            size="small"
                            label={language === 'my'
                              ? `${Math.round(plan.duration_hours / 24)} hari`
                              : `${Math.round(plan.duration_hours / 24)} days`
                            }
                            sx={{ mt: 0.5, height: 22, fontSize: '0.7rem', bgcolor: alpha(color, 0.08) }}
                          />
                        )}
                      </Box>

                      {/* Price */}
                      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                        <Typography variant="h4" fontWeight={700} color={color}>
                          {plan.price > 0 ? `RM${plan.price}` : txt.free}
                        </Typography>
                      </Box>

                      <Divider sx={{ mb: 1.5 }} />

                      {/* Features List */}
                      <Box sx={{ flex: 1, mb: 2 }}>
                        {[
                          { label: txt.basicEstimates, has: true, detail: limits.quick_estimates_per_month === -1 ? txt.unlimited : `${limits.quick_estimates_per_month} ${txt.usesOnly}` },
                          { label: txt.materialEstimator, has: !!features.material_estimator },
                          { label: txt.fullBreakdown, has: !!features.full_breakdown },
                          { label: txt.saveProjects, has: !!features.save_projects },
                          { label: txt.pdfExport, has: !!features.pdf_export },
                          { label: txt.compareScenarios, has: !!features.compare_scenarios },
                        ].map((f, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            {f.has ? (
                              <Check sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                              <Close sx={{ fontSize: 16, color: 'action.disabled' }} />
                            )}
                            <Typography
                              variant="body2"
                              fontSize={12}
                              color={f.has ? 'text.primary' : 'text.disabled'}
                            >
                              {f.label}
                              {f.has && f.detail && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({f.detail})
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant={isCurrent ? 'outlined' : 'contained'}
                        fullWidth
                        disabled={isCurrent || plan.plan_code === 'free' || paymentLoading === plan.plan_code}
                        onClick={() => handleGetAccess(plan.plan_code)}
                        sx={{
                          bgcolor: isCurrent ? 'transparent' : color,
                          borderColor: color,
                          color: isCurrent ? color : 'white',
                          '&:hover': {
                            bgcolor: isCurrent ? alpha(color, 0.1) : color,
                            opacity: isCurrent ? 1 : 0.9,
                          },
                          '&.Mui-disabled': {
                            bgcolor: isCurrent ? 'transparent' : undefined,
                            borderColor: isCurrent ? color : undefined,
                            color: isCurrent ? color : undefined,
                          },
                        }}
                      >
                        {paymentLoading === plan.plan_code ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : isCurrent ? (
                          txt.currentPlan
                        ) : plan.price > 0 ? (
                          txt.getAccess
                        ) : (
                          txt.free
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{txt.loading}</Typography>
          </Box>
        )}
      </Box>

      {/* ═══════════════════════════════════════════════════════
          SECTION 4: Redeem Code (compact, at bottom)
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.12),
      }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1.5, sm: 2 },
          }}>
            {/* Label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <CardGiftcard sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                {txt.redeemCode}
              </Typography>
            </Box>

            {/* Input + Button */}
            <Box sx={{ display: 'flex', gap: 1, flex: 1, width: '100%' }}>
              <TextField
                size="small"
                placeholder={txt.redeemPlaceholder}
                value={redeemInput}
                onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
                disabled={redeemLoading}
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600 } }}
              />
              <Button
                variant="contained"
                onClick={handleRedeem}
                disabled={redeemLoading || !redeemInput.trim()}
                sx={{ minWidth: 80, height: 40 }}
              >
                {redeemLoading ? <CircularProgress size={20} color="inherit" /> : txt.redeem}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Phone Dialog */}
      <Dialog open={phoneDialogOpen} onClose={() => { setPhoneDialogOpen(false); setPendingPlanCode(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{user?.phone ? txt.editPhone : txt.addPhone}</DialogTitle>
        <DialogContent>
          {pendingPlanCode && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<Warning />}>
              {txt.phoneRequired}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={txt.phoneNumber}
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value.replace(/[^0-9+]/g, ''))}
            placeholder="0123456789"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPhoneDialogOpen(false); setPendingPlanCode(null); }}>{txt.cancel}</Button>
          <Button variant="contained" onClick={handleSavePhone} disabled={phoneLoading || !phoneInput || phoneInput.length < 9}>
            {phoneLoading ? <CircularProgress size={20} /> : txt.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountPage;
